import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { uploadVerificationDoc } from '../../util/api';
import { AspectRatioWrapper, IconClose, ValidationError } from '../../components';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import css from './VerificationDocsInput.module.css';

const RemoveImageButton = props => {
  const { onClick } = props;
  return (
    <button
      className={css.removeImage}
      onClick={onClick}
      type="button"
      aria-label="Remove document"
    >
      <IconClose size="small" />
    </button>
  );
};

const VerificationDocsInput = props => {
  const { input, meta, label, tip, disabled, className } = props;
  const { value, onChange } = input;
  const intl = useIntl();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const documentUrl = typeof value === 'string' ? value : '';
  const aspectWidth = 1;
  const aspectHeight = 1;

  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const file = files[0];
    setError(null);

    // Validate: images only
    if (!file.type.startsWith('image/')) {
      setError(
        intl.formatMessage({ id: 'EditListingDetailsForm.verificationDocsImageOnlyError' })
      );
      e.target.value = '';
      return;
    }

    // Validate: max 10 MB
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(
        intl.formatMessage(
          { id: 'EditListingDetailsForm.verificationDocsFileTooLargeError' },
          { maxSize: MAX_FILE_SIZE_MB }
        )
      );
      e.target.value = '';
      return;
    }

    setUploading(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result;

      uploadVerificationDoc({ file: base64Data })
        .then(res => {
          if (res.secure_url) {
            onChange(res.secure_url);
          } else {
            throw new Error('Upload returned empty secure URL');
          }
          setUploading(false);
        })
        .catch(err => {
          console.error('Document upload failed:', err);
          setError(
            intl.formatMessage({ id: 'EditListingDetailsForm.verificationDocsUploadFailed' })
          );
          setUploading(false);
        });
    };
    reader.onerror = () => {
      setError('File reading failed.');
      setUploading(false);
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  const handleRemove = e => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={classNames(css.root, className)}>
      <label className={css.label}>{label}</label>
      {tip && <p className={css.tip}>{tip}</p>}

      <div className={css.imagesFieldArray}>
        {documentUrl ? (
          <div className={css.thumbnailWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              <a href={documentUrl} target="_blank" rel="noopener noreferrer" title="View document">
                {documentUrl.match(/\.(jpeg|jpg|gif|png|webp)/i) ? (
                  <img src={documentUrl} alt={label} className={css.thumbnail} />
                ) : (
                  <IconCard className={css.docIcon} />
                )}
              </a>
              <RemoveImageButton onClick={handleRemove} />
              <div className={css.fileName}>{documentUrl.split('/').pop()}</div>
            </AspectRatioWrapper>
          </div>
        ) : (
          <div className={css.addImageWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              <input
                id={`verificationDocsInputFile-${input.name}`}
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className={css.fileInput}
                onChange={handleFileChange}
                disabled={disabled || uploading}
              />
              <label htmlFor={`verificationDocsInputFile-${input.name}`} className={css.addImage}>
                <div className={css.chooseImageText}>
                  <span className={classNames(css.chooseImage, 'h4')}>
                    <FormattedMessage id="EditListingDetailsForm.verificationDocsUploadTip" />
                  </span>
                  <span className={classNames(css.imageTypes, 'marketplaceTinyFontStyles')}>
                    <FormattedMessage id="EditListingDetailsForm.verificationDocsSingleLimitTip" />
                  </span>
                </div>
              </label>
              {uploading && (
                <div className={css.thumbnailLoading}>
                  <div className={css.spinner} />
                </div>
              )}
            </AspectRatioWrapper>
          </div>
        )}
      </div>

      {error && <div className={css.error}>{error}</div>}
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

VerificationDocsInput.defaultProps = {
  disabled: false,
  className: null,
  tip: null,
};

VerificationDocsInput.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default VerificationDocsInput;
